import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import SearchPanel from './SearchPanel';
import SettingsPanel from './SettingsPanel';

interface DashboardLayoutProps {
  children: React.ReactNode;
  currentPage?: string;
  onNavigate?: (page: string) => void;
  activeSection?: string;
  onSectionChange?: (section: string) => void;
}

export interface ThemeSettings {
  primaryColor: string;
  secondaryColor: string;
  fontSize: string;
  fontFamily: string;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  currentPage = 'home',
  onNavigate,
  activeSection,
  onSectionChange
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [searchOpen, setSearchOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [theme, setTheme] = useState<ThemeSettings>({
    primaryColor: '#003366',
    secondaryColor: '#0066CC',
    fontSize: '16px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  });

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div
      className="min-h-screen bg-gray-100"
      style={{ fontFamily: theme.fontFamily, fontSize: theme.fontSize }}
    >
      <Header
        onMenuClick={() => setSidebarOpen(!sidebarOpen)}
        onSearchClick={() => setSearchOpen(!searchOpen)}
        onSettingsClick={() => setSettingsOpen(!settingsOpen)}
        primaryColor={theme.primaryColor}
        currentPage={currentPage}
        onNavigate={onNavigate}
      />

      <div className="flex relative">
        {/* Mobile overlay */}
        {isMobile && sidebarOpen && (
          <div
            className="fixed inset-0 top-[60px] bg-black/50 z-[85]"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <Sidebar
          isOpen={sidebarOpen}
          primaryColor={theme.primaryColor}
          secondaryColor={theme.secondaryColor}
          currentPage={currentPage}
          onNavigate={onNavigate}
          activeSection={activeSection}
          onSectionChange={onSectionChange}
        />

        <main
          className="flex-1 p-8 min-h-[calc(100vh-60px)] transition-all duration-300"
          style={{
            marginLeft: !isMobile && sidebarOpen ? '280px' : '0',
            width: !isMobile && sidebarOpen ? 'calc(100% - 280px)' : '100%',
          }}
        >
          {children}
        </main>
      </div>

      {searchOpen && (
        <SearchPanel
          onClose={() => setSearchOpen(false)}
          primaryColor={theme.primaryColor}
        />
      )}

      {settingsOpen && (
        <SettingsPanel
          onClose={() => setSettingsOpen(false)}
          theme={theme}
          onThemeChange={setTheme}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
